/* eslint-disable @typescript-eslint/comma-dangle */
/* eslint-disable react/no-unknown-property */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import * as THREE from 'three'
import gsap from 'gsap'
import { Canvas, invalidate, SceneProps, useFrame } from '@react-three/fiber'
import { useData } from '../context/DataContext'
import {
  fragmentShader,
  fragmentShaderSilverado,
  fragmentVerticalShader,
  fragmentVerticalShaderSilverado,
  vertexShader,
} from '../videoplayer/SplitShaders'
import UIButton from '../ui/UIButton'
import { IVideoData } from '../@types/data'
import { IEnv } from '../@types/env'

type ISplitVideoProps = SceneProps & {
  texture?: THREE.VideoTexture
}

enum ActiveStates {
  NONE = 'NONE',
  PERFORMANCE = 'PERFORMANCE',
  DURABILITY = 'DURABILITY',
}

const ANIMATION_FRAME_LENGTH = 18
const START_INDEX = 30
let targetValue = 0.5
let targetFrame = START_INDEX

const LandingPage: React.FC = () => {
  const { isLowRes, copy, videos, setRequestedVideo } = useData()
  const { REACT_APP_CAR_TYPE, REACT_APP_VIDEO_VERSION } =
    process.env as unknown as IEnv

  const videoMainRef = useRef<HTMLVideoElement>(null)
  const engineRef = useRef<HTMLDivElement>(null)
  const buttonContainerRef = useRef<HTMLDivElement>(null)
  const selectedFrameRef = useRef<number>(START_INDEX)
  const targetOffset = useRef<number>(0.5)
  const shouldMove = useRef<boolean>(true)
  const isPortrait = useRef<boolean>(false)
  const [selectedFrame, setSelectedFrame] = useState(START_INDEX)
  const [isActive, setActive] = useState(ActiveStates.NONE)
  const [isPortraitState, setIsPortrait] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [preRequested, setPreRequested] = useState<IVideoData | undefined>()
  const postFix: string = isLowRes ? '-720p' : ''
  const INTRO_VIDEO = `./videos/${REACT_APP_CAR_TYPE}-intro${postFix}.mp4?cb=video-v${REACT_APP_VIDEO_VERSION}`

  const texture: THREE.VideoTexture | undefined = useMemo(() => {
    if (videoMainRef?.current != null) {
      const newTexture = new THREE.VideoTexture(videoMainRef.current)
      newTexture.minFilter = THREE.LinearFilter
      newTexture.magFilter = THREE.LinearFilter
      return newTexture
    }
  }, [videoMainRef, videoMainRef.current])

  const material = useRef<THREE.ShaderMaterial>(null)
  const uniforms = useMemo(() => {
    return {
      u_texture: {
        type: 't',
        value: texture,
      },
      u_offset: {
        type: 'f',
        value: 0.5,
      },
      u_alpha: {
        type: 'f',
        value: 0.7,
      },
      u_target_offset: {
        type: 'f',
        value: 0.5,
      },
    }
  }, [texture])

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseout', handleMouseOut)
    window.addEventListener('resize', handleResize)
    handleResize()
    setTimeout(() => {
      handleResize()
    }, 1000)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseout', handleMouseOut)
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    if (videos == null) return
    if (preRequested != null) {
      let targetValue = 0.5
      if (preRequested.key === videos[0].key) {
        targetValue = isPortrait.current ? 0 : 1
      } else if (preRequested.key === videos[1].key) {
        targetValue = isPortrait.current ? 1 : 0
      }

      if (material.current == null) return
      gsap.killTweensOf(material.current?.uniforms.offset)
      gsap.to(material.current.uniforms.u_offset, {
        value: targetValue,
        duration: 0.5,
        delay: 0.2,
        onComplete: () => {
          if (material.current == null) return
          material.current.uniforms.u_offset.value = targetValue
          setIsPaused(true)
          setRequestedVideo(preRequested)
        },
      })
      // gsap.to(engineRef.current, {
      //   x: `${isPortrait ? 50 : targetValue * 100}%`,
      //   y: `${isPortrait ? `${60 - targetValue * 60}vh` : `${0}%`}`,
      //   // opacity: targetValue === 0.5 ? 1 : 0.5,
      //   opacity: 0,
      //   duration: 0.5,
      //   delay: 0.3,
      // })
    }
  }, [preRequested])

  useEffect(() => {
    const engineWidth = isPortrait.current
      ? innerHeight * 0.4
      : innerHeight * 0.6
    const offset = isPortrait.current
      ? (engineWidth * 0.1) / innerHeight
      : (engineWidth * 0.1) / innerWidth
    targetOffset.current = offset / 2
    if (material.current == null) return
    material.current.uniforms.u_target_offset.value = targetOffset.current
  }, [isPortrait.current])

  const handleResize = (): void => {
    const { innerWidth, innerHeight } = window
    const portrait = innerHeight > innerWidth && innerWidth < 640
    const engineWidth = portrait ? innerHeight * 0.4 : innerHeight * 0.6
    const offset = portrait
      ? (engineWidth * 0.1) / innerHeight
      : (engineWidth * 0.1) / innerWidth
    setIsPortrait(portrait)
    isPortrait.current = portrait
    targetOffset.current = offset / 2
    if (material.current == null) return
    material.current.uniforms.u_target_offset.value = targetOffset.current
  }

  const SplitVideoScene: React.FC<ISplitVideoProps> = () => {
    useFrame(({ gl, scene, camera }) => {
      gl.render(scene, camera)
      if (!isPaused) invalidate()
    }, 1)

    useEffect(() => {
      if (!isPaused) invalidate()
    }, [isPaused])

    const horizontalShader =
      REACT_APP_CAR_TYPE === 'silverado'
        ? fragmentShaderSilverado
        : fragmentShader
    const verticalShader =
      REACT_APP_CAR_TYPE === 'silverado'
        ? fragmentVerticalShaderSilverado
        : fragmentVerticalShader

    return (
      <mesh>
        <planeGeometry args={isPortraitState ? [9, 16, 1] : [16, 9, 1]} />
        <shaderMaterial
          ref={material}
          attach="material"
          uniforms={uniforms}
          fragmentShader={isPortraitState ? verticalShader : horizontalShader}
          vertexShader={vertexShader}
          blending={THREE.NormalBlending}
          transparent={true}
        />
      </mesh>
    )
  }

  const killTweens = (): void => {
    document.removeEventListener('mousemove', handleMouseMove)
    shouldMove.current = false
    if (material.current == null) return
    gsap.killTweensOf(material.current?.uniforms.offset)
  }

  const handleMouseMove = (e: MouseEvent): void => {
    if (preRequested != null || !shouldMove.current) return
    const prevTargetValue = targetValue
    const mousePosition = isPortrait.current
      ? e.clientY / window.innerHeight
      : e.clientX / window.innerWidth
    if (mousePosition < 0.5) {
      setActive(ActiveStates.PERFORMANCE)
      targetValue = isPortrait.current
        ? 0.5 - targetOffset.current
        : 0.5 + targetOffset.current
      targetFrame = START_INDEX
    } else if (mousePosition === 0.5) {
      targetValue = 0.5
      targetFrame = START_INDEX + Math.floor(ANIMATION_FRAME_LENGTH / 2)
      setActive(ActiveStates.NONE)
    } else if (mousePosition > 0.5) {
      setActive(ActiveStates.DURABILITY)
      targetValue = isPortrait.current
        ? 0.5 + targetOffset.current
        : 0.5 - targetOffset.current
      targetFrame = START_INDEX + ANIMATION_FRAME_LENGTH - 1
    }

    if (prevTargetValue === targetValue) return
    animate()

    // gsap.to(engineRef.current, {
    //   x: `${isPortrait ? 50 : targetValue * 100}%`,
    //   y: `${isPortrait ? `${60 - targetValue * 60}vh` : `${0}%`}`,
    //   // opacity: targetValue === 0.5 ? 1 : 0.5,
    //   duration: 1.2,
    // })
  }

  const handleMouseOut = (): void => {
    targetValue = 0.5
    targetFrame = START_INDEX
    animate()
  }

  const animate = (): void => {
    if (material.current == null) return
    gsap.to(material.current.uniforms.u_offset, {
      value: targetValue,
      duration: 0.4,
    })

    const newFrameValue = {
      value: selectedFrameRef.current,
    }

    gsap.to(newFrameValue, {
      value: targetFrame,
      duration: 0.25,
      roundProps: { value: 1 },
      onUpdate: () => {
        selectedFrameRef.current = newFrameValue.value
        setSelectedFrame(selectedFrameRef.current)
      },
    })
  }

  const hideAnimation = 'transition-opacity duration-200 opacity-0'

  return (
    <div className="w-full flex-1">
      <div className="w-full pb-[56.25%]">
        <div
          className={`absolute flex justify-center items-center inset-0 delay-[500ms] ${
            preRequested != null ? hideAnimation : ''
          }`}
        >
          <Canvas
            gl={{ antialias: true, toneMapping: THREE.NoToneMapping }}
            frameloop="demand"
            linear
          >
            <SplitVideoScene texture={texture} />
          </Canvas>
          <div
            ref={engineRef}
            className={`absolute w-full flex items-center translate-x-1/2 ${
              preRequested != null ? hideAnimation : ''
            }`}
          >
            <img
              className="absolute w-[60vh] h-[60vh] -ml-[30vh] lg:w-[100vh] lg:h-[100vh] lg:-ml-[50vh] "
              src="./images/engine-shadow.png"
            />
            <div className="relative w-[40vh] h-[40vh] -ml-[20vh] lg:w-[60vh] lg:h-[60vh] lg:-ml-[30vh] overflow-hidden">
              {Array.from(Array(ANIMATION_FRAME_LENGTH)).map((n, index) => (
                <img
                  key={`engine-${index}`}
                  className={`absolute w-full h-full top-0 left-0 ${
                    START_INDEX + index === selectedFrame
                      ? 'opacity-100'
                      : 'opacity-0'
                  }`}
                  src={`./images/${REACT_APP_CAR_TYPE}-engine-${
                    START_INDEX + index
                  }.png`}
                />
              ))}
            </div>
          </div>
          {copy != null && String(copy.LANDING_DISCLOSURE).length > 0 && (
            <h6 className="absolute bottom-6 text-center drop-shadow-lg">
              {copy.LANDING_DISCLOSURE}
            </h6>
          )}
        </div>
        {videos != null && (
          <div ref={buttonContainerRef}>
            <UIButton
              label={videos[0].label}
              style={`transition-opacity ${
                preRequested != null ? hideAnimation : ''
              } ${
                isActive === ActiveStates.DURABILITY && preRequested == null
                  ? 'opacity-70'
                  : 'opacity-100'
              }`}
              alignLeft
              onClick={() => {
                killTweens()
                setPreRequested(videos[0])
              }}
            />
            <UIButton
              label={videos[1].label}
              style={`transition-opacity ${
                preRequested != null ? hideAnimation : ''
              } ${
                isActive === ActiveStates.PERFORMANCE && preRequested == null
                  ? 'opacity-70'
                  : 'opacity-100'
              }`}
              onClick={() => {
                killTweens()
                setPreRequested(videos[1])
              }}
            />
          </div>
        )}
      </div>
      <video
        ref={videoMainRef}
        src={INTRO_VIDEO}
        className="absolute opacity-[0.001] top-0 -z-50"
        loop={true}
        muted
        playsInline
        autoPlay
        crossOrigin="anonymous"
      />
    </div>
  )
}

export default LandingPage
